import { ChakraProvider } from "@chakra-ui/react";
import { Fonts, theme } from "@airhelp/react";
import { storyblokInit } from "@storyblok/react";
import config from "config/config";
import { LocalizationContextProvider } from "contexts/LocalizationContext";
import { SettingsContextProvider } from "contexts/SettingsContext";
import { useExtendedRouter } from "hooks";
import useGTM from "hooks/useGTM";
import useRollbar from "hooks/useRollbar";
import useUserTrackingCookie from "hooks/useUserTrackingCookie";
import CookiesNoticeAdapter from "modules/common/components/CookiesNoticeAdapter";
import type { AppProps } from "next/app";
import Error from "next/error";
import Head from "next/head";
import Script from "next/script";
import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import type { DatasourceProps } from "types/datasource";
import type { PageProps } from "types/global";
import showAntiFlickerScript from "utils/serverSideUtils/datasources/showAntiFlickerScript";

const renderAntiFlickerScript = (antiFlickerSettings: DatasourceProps[], gtmCode: string, pagePath: string) => {
  const showAntiFlicker = showAntiFlickerScript(antiFlickerSettings, pagePath);

  const antiFlickerScript = `
    (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
    h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
    (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
    })(window,document.documentElement,'async-hide','dataLayer',4000,
    {'${gtmCode}':true});
  `;

  return (
    showAntiFlicker && (
      <>
        <Script
          key="optimize-anti-flicker"
          id="optimize-anti-flicker"
          dangerouslySetInnerHTML={{ __html: antiFlickerScript }}
        />
        <style key="optimize-anti-flicker-styles">{`.async-hide { opacity: 0 !important}`}</style>
      </>
    )
  );
};

const App = ({ Component, pageProps }: AppProps<PageProps & { pageType: string; errorCode: number | null }>) => {
  const {
    translations,
    language = "en",
    pageType,
    errorCode = null,
    isTrackingless = false,
    settings = {},
    antiFlickerSettings,
  } = pageProps;

  const { asPath: pagePath } = useExtendedRouter();

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useGTM(language, pageType, isTrackingless);
  useUserTrackingCookie();
  useRollbar();
  storyblokInit();

  if (errorCode) return <Error statusCode={errorCode} />;

  return (
    <>
      <Head>
        {renderAntiFlickerScript(antiFlickerSettings, config.gtmCode, pagePath)}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <CookiesProvider>
        <ChakraProvider theme={theme}>
          <Fonts />
          <SettingsContextProvider settings={settings}>
            <LocalizationContextProvider language={language} translations={translations}>
              <Component {...pageProps} />
              <CookiesNoticeAdapter />
            </LocalizationContextProvider>
          </SettingsContextProvider>
        </ChakraProvider>
      </CookiesProvider>
    </>
  );
};

export default App;
